import type { LucideIcon } from "lucide-react";
import { forwardRef, useId } from "react";

import { cn } from "@/lib/utils";
import { Label } from "./label";

type TDefaultInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix">;

type TInputMessageType = "success" | "error" | "info";

export type InputProps = TDefaultInputProps & {
	label?: string;
	icon?: LucideIcon;
	message?: string;
	messageType?: TInputMessageType;
	prefix?: React.ReactNode;
	classNames?: { container?: string; inputContainer?: string; input?: string; label?: string };
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			type = "text",
			icon: Icon,
			prefix,
			label,
			message,
			messageType = "error",
			classNames,
			...props
		},
		ref
	) => {
		const id = useId();

		return (
			<div className={classNames?.container}>
				<Label id={id} className={classNames?.label}>
					{label}
				</Label>
				<div
					className={cn(
						"flex rounded-md bg-white shadow-sm ring-1 ring-inset ring-gray-300",
						"transition-all duration-150 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-300",
						props.disabled ? "cursor-not-allowed bg-gray-100 text-gray-500" : "text-gray-900",
						message && messageType === "error" && "ring-red-600",
						classNames?.inputContainer
					)}
				>
					{Icon && (
						<Icon className={cn("h-5 w-5", props.disabled ? "text-inherit" : "text-gray-500")} />
					)}

					{prefix}

					<input
						id={id}
						ref={ref}
						type={type}
						className={cn(
							"block w-full flex-1 border-0 bg-transparent px-3 py-1.5 text-gray-900 outline-none placeholder:text-gray-400",
							"focus:ring-0 sm:text-sm sm:leading-6",
							props.disabled && "cursor-not-allowed",
							classNames?.input
						)}
						{...props}
					/>
				</div>

				{message && message.length > 1 && (
					<p
						className={cn(
							"mt-2 text-sm",
							messageType === "error"
								? "text-red-600"
								: messageType === "success"
								? "text-green-600"
								: "text-blue-600"
						)}
					>
						{message}
					</p>
				)}
			</div>
		);
	}
);
